import {Model, ResourceModel} from "@bryntum/schedulerpro";
import {HasProjectMixin} from "./mixins/HasProjectMixin";
import {ModelTagsMixin} from "./mixins/ModelTagsMixin";
import {STRIPED_TASK_STYLE, TASK_TYPES} from "../../other/constants/TaskTypes.jsx";
import {STORE_KEYS} from "../../other/constants/StoreKeys.jsx";
import {ModelEntityReminderStatusMixin} from "./mixins/ModelEntityReminderStatusMixin.jsx";
import {getAging, getSwivuEmptyStringValue} from "../../other/utils/utils.jsx";
import {TaskBudget} from "./TaskBudget.jsx";
import { TASK_BUDGET_FIELDS } from "../../other/constants/OtherKeys.jsx";
import { HasIconMixin } from "./mixins/HasIconMixin.jsx";
import { HasChecklistMixin } from "./mixins/HasChecklistMixin.jsx";
import HasCompanyCam from "@/bryntum-lib/models/mixins/HasCompanyCam.jsx";
import { BoardUser } from "@/modern/components/user-avatar";
import { Location } from "@/bryntum-lib/models/Location";

const DEFAULT_TASK_COLOR = "#B71C1C";
const BUDGET_ID_FIELD = "budgetRecord";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildTaskModel = (base: any) => class GeneratedTaskModel extends HasProjectMixin(HasCompanyCam(ModelTagsMixin(ModelEntityReminderStatusMixin(HasChecklistMixin(HasIconMixin(base)))))) {

  declare isPhantom: boolean;
  declare id: number | string;
  declare startDate: Date;
  declare endDate: Date;
  declare clientId: number;
  declare priority: number;
  declare description: string;
  declare point_of_contact: string;
  declare notes: string;
  declare duration: number;
  declare order: number;
  declare type: number;
  declare assignmentCount: number;
  declare creatorId: number;
  declare createdAt: Date;
  declare updaterId: number;
  declare updatedAt: Date;
  declare lastEventUpdatedBy: number;
  declare lastEventUpdatedAt: Date;
  declare agendaTitle: string;
  declare includePhotos: boolean;
  declare includeDocuments: boolean;
  declare budgetId?: number;
  declare checklistCount: number;
  declare checklistStatus: number;
  declare reRender: boolean;
  declare locationId?: number;
  declare creatorRecord: BoardUser;
  declare updaterRecord: BoardUser;
  declare lastEventUpdatedByRecord: BoardUser;
  declare budgetRecord: TaskBudget;

  declare isTask: boolean;

  static get relations() {
    return {
      creatorRecord: {
        foreignKey: "creatorId",
        foreignStore: STORE_KEYS.boardUserStore,
      },
      updaterRecord: {
        foreignKey: "updaterId",
        foreignStore: STORE_KEYS.boardUserStore,
      },
      lastEventUpdatedByRecord: {
        foreignKey: "lastEventUpdatedBy",
        foreignStore: STORE_KEYS.boardUserStore,
        relatedCollectionName: "taskLastUpdatedBys",
      },
      budgetRecord: {
        foreignKey: "budgetId",
        foreignStore: STORE_KEYS.taskBudgetStore,
        relatedCollectionName: "budgetTasks"
      }
    }
  }

  static get fields() {
    return [
      { name: "name", dataSource: "display_name" },
      { name: "startDate", dataSource: "start_date", type: "date" },
      { name: "endDate", dataSource: "end_date", type: "date" },
      { name: "clientId", dataSource: "client" },
      { name: "priority" },
      { name: "description", convert: getSwivuEmptyStringValue },
      "point_of_contact",
      { name: "color" },
      "notes",
      { name: "archived", type: "boolean", defaultValue: false },
      { name: "duration", type: "number" },
      { name: "order" },
      { name: "type", dataSource: "task_type", defaultValue: TASK_TYPES.TASK },
      { name: "assignmentCount", dataSource: "assignment_count", type: "number" },
      { name: "creatorId", dataSource: "creator", type: "number"},
      { name: "createdAt", dataSource: "created_at", type: "date" },
      { name: "updaterId", dataSource: "updater", type: "number"},
      { name: "updatedAt", dataSource: "updated_at", type: "date" },
      { name: "lastEventUpdatedBy", dataSource: "last_event_updated_by", type: "number"},
      { name: "lastEventUpdatedAt", dataSource: "last_event_updated_at", type: "date"},
      { name: "taskStyle", dataSource: "style" },
      { name: "agendaTitle", dataSource: "agenda_title" },
      { name: "includePhotos", dataSource: "include_photo"},
      { name: "includeDocuments", dataSource: "include_document", defaultValue: false},
      { name: "aging", persist: false },
      { name: "budgetId", dataSource: "budget" },
      // This field is toggled when changes are made in the budget label settings
      { name: "reRender", persist: false, type: "boolean" },
      { name: "locationId", dataSource: "location" },
    ];
  }

  get companyCamIntegrationStore() {
    return this.project?.taskIntegrationsStore;
  }

  get location(): Location | null {
    return this.project?.locationStore.getById(this.locationId) ?? null;
  }

  get locationValue() {
    return this.location?.locationValue;
  }

  set location(value: Location | null) {
    this.set("locationId", value?.id ?? value);
  }

  static swivuGetFieldOriginalSourceName(fieldName: string) {
    if (fieldName.startsWith(BUDGET_ID_FIELD)) {
      const [, budgetFieldName] = fieldName.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (TaskBudget as any).getFieldDefinition?.(budgetFieldName)?.dataSource;
    }
    const fieldsMaps: Record<string, string> = {
      clientName: "client",
      isTaskScheduled: "assignment_count",
      [TASK_BUDGET_FIELDS.title]: "budget_id"
    }
    return fieldsMaps[fieldName] || super.swivuGetFieldOriginalSourceName?.(fieldName);
  }

  get entityVisibleName() {
    return "Task";
  }

  get tagStoreName() {
    return STORE_KEYS.taskTagValuesStore;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get client(): any {
    return this.project?.clientsStore.getById(this.get("clientId"));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set client(value: any) {
    // setting client and client contacts for task
    this.set("clientId", value)
  }

  /* This getter is used in the task editor where budget model was required using task id.
    Problem: In task editor budget field is used; Once the record is set on to the task editor, It is defaulting to budget's id
    as the display value of the field since budget store data is assigned to combo later.
    To fix this we are using this 'budget' record getter for this task record it solves the field defaulting to records id as display value.
  */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get budget(): any {
    return this.project?.taskBudgetStore.getById(this.get("budgetId"));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set budget(value: any) {
    this.set("budgetId", value);
  }

  get clientName() {
    return this.client?.name;
  }

  get color() {
    const taskColor = this.get("color");
    //Applied fix for invalid hex value colors for existing tasks
    const color = (taskColor && !["#00005x", "#52527x", "#96969x", "#96969x", "#00011x", "#00003x", "#00013x"].includes(taskColor)) ?
      taskColor : DEFAULT_TASK_COLOR;
    return color;
  }

  set color(value: string) {
    this.set("color", value);
  }

  get taskStyle() {
    return (!this.get("taskStyle") && this.isTimeEvent) ? STRIPED_TASK_STYLE : this.get("taskStyle");
  }

  set taskStyle(value: string) {
    this.set("taskStyle", value);
  }

  get name() {
    return this.get("name");
  }

  set name(value: string) {
    this.set("name", value);
  }

  get archived() {
    return this.get("archived");
  }

  set archived(value: boolean) {
     /* If task is being archived, then delink from budget */
    if(value) {
      this.set("budgetId", null);
      this.set("clientId", null);
    }
    this.set("archived", value);
  }

  // isScheduled is a reserved field
  get isTaskScheduled() {
    return this.assignmentCount > 0;
  }

  get taskTagValues() {
    return this.project?.taskTagValues;
  }

  // Returns planned events for Plan tab in the task editor
  get planEvents() {
    // We are trusting backend data. If backend returned us duplicate records this code will cause a duplicate id error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.project?.planAssignmentStore as any)?.forEach((assignment: any) => {
      if (assignment.resourceId === this.id) {
        result.push(assignment.event);
      }
    });
    //Applying sort based on order key returned from backend to persist the reorder feature
    if(result.length){
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.sort((a: any, b: any) => {
        if(a.order < b.order) return -1;
        if(a.order > b.order) return 1;
        return 0;
      })
    }

    return result;
  }

  get isTimeEvent() {
    /*
    Changing Task type to 1, Because existing time events has type 2 due to that
    it assigns the striped style to it even when the task style is removed
    */
    if (this.type !== 1) {
      this.set("type", 1);
      this.set("taskStyle", STRIPED_TASK_STYLE);
    }
    return this.get("taskStyle") === STRIPED_TASK_STYLE;
  }

  // make sure standard toJSON method never leaves store instances
  toJSON() {
    const result = super.toJSON();

    if (result.baselines) {
      result.baselines = result.baselines.toJSON ? result.baselines.toJSON() : result.baselines;
    }

    return result;
  }


  get aging() {
    return getAging(this.get("createdAt"));
  }

  get [TASK_BUDGET_FIELDS.title]() {
    const budgetId = this.get("budgetId");
    const project = this.project;
    let title = "";
    if (project && budgetId) {
      const budgetRecord = project.taskBudgetStore.getById(budgetId);
      title = budgetRecord?.[TASK_BUDGET_FIELDS.title];
    }
    return title;
  }

  get planningDatesRecords() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.project ? (this.project.taskPlanningDatesStore as any).query((record: any) => record.taskId === this.id, true) : [];
  }

  get reminderNotifyUsers() {
    if (!this.project) return [];

    // Query notifyeeboardusers store for records matching this task and reminder updates enabled
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const notifyRecords = (this.project.notfiyeeBoardUserStore as any).query((record: any) =>
      record.taskId === this.id && record.isReminderUpdates === true
    );

    // Extract user names from boardUserRecord
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return notifyRecords.map((record: any) => record.boardUserName);
  }
}

export class Task extends buildTaskModel(Model) {
  static get $name() {
    return "Task";
  }

  get events() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.project?.eventStore.reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (accum: any, event: any) => event.taskId === this.id ? accum.concat(event): accum, []
    );
  }

  get entityType() {
    return 'taskId' as const;
  }
}

// this model is used in the plan view, it should extend resource model
export class PlanTaskModel extends buildTaskModel(ResourceModel) {
  static get $name() {
    return "PlanTaskModel";
  }


  get entityType() {
    return 'taskId';
  }
}
